import { FC, memo, useEffect, useRef } from 'react';
import { Box, SxStyleProp } from 'theme-ui';
import { clamp } from 'ramda';
import { compareNumbers } from 'utils/math';

export type RangeProps = {
  min?: number;
  max: number;
  value1: number;
  value2: number;
  onChange: (value1: number, value2: number) => void;
  className?: string;
  minDiff?: number;
};

export const BORDER_WIDTH = 2;
export const HANDLE_WIDTH = 16;

const trackStyles: SxStyleProp = {
  position: 'relative',
  bg: 'muted',
  borderRadius: 2,
  height: 24
};

const fillerStyles: SxStyleProp = {
  position: 'absolute',
  top: 0,
  height: '100%',
  boxSizing: 'border-box',
  borderWidth: BORDER_WIDTH,
  borderStyle: 'solid',
  borderColor: 'primary',
  borderLeft: 'none',
  borderRight: 'none',
  bg: 'primary'
};

const handleStyles: SxStyleProp = {
  position: 'absolute',
  top: `${-BORDER_WIDTH}px`,
  width: HANDLE_WIDTH,
  height: '100%',
  bg: 'primary',
  borderWidth: BORDER_WIDTH,
  borderStyle: 'solid',
  borderColor: 'primary',
  boxSizing: 'content-box',

  '&::before, &::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 1,
    height: 12,
    width: 2,
    borderRadius: 1,
    bg: 'background'
  },

  '&::before': {
    left: '5px'
  },

  '&::after': {
    right: '5px'
  }
};

export const Range: FC<RangeProps> = memo(
  ({ value1, value2, min = 0, max, onChange, minDiff = 0, ...props }) => {
    const { abs } = Math;

    const boxRef = useRef<HTMLDivElement>(null);
    const changeHandler = useRef<(val: number, normalization: number) => void>(null);

    useEffect(() => {
      changeHandler.current = (val, normalization) => {
        let [start, end] = [value1, value2].sort(compareNumbers);

        if (abs(val - start) < abs(val - end)) {
          start = val + normalization;
        } else {
          end = val - normalization;
        }

        if (end - start < minDiff) {
          end = start + minDiff;
        }

        if (end - start > minDiff && start <= end) {
          onChange(start, end);
        }
      };
    }, [onChange, value1, value2, max, min]);

    useEffect(() => {
      const handleWBorders = HANDLE_WIDTH + 2 * BORDER_WIDTH;
      const rangeLength = max - min;

      const handlePointerMove = (e: PointerEvent) => {
        const BCRect = boxRef.current.getBoundingClientRect();
        const handleFactor = handleWBorders / 2 / BCRect.width;

        const factor =
          (e.pageX - BCRect.left - handleWBorders) / (BCRect.width - 2 * handleWBorders);
        const val = Math.floor(clamp(-handleFactor, 1 + handleFactor, factor) * rangeLength);

        const normalization = Math.ceil(handleFactor * rangeLength);

        changeHandler.current?.(val, normalization);
      };

      const handlePointerDown = (e: PointerEvent) => {
        handlePointerMove(e);
        window.addEventListener('pointermove', handlePointerMove);
      };

      const handlePointerUp = () => {
        window.removeEventListener('pointermove', handlePointerMove);
      };

      boxRef.current.addEventListener('pointerdown', handlePointerDown);
      window.addEventListener('pointerup', handlePointerUp);

      return () => {
        boxRef.current?.removeEventListener('pointerdown', handlePointerDown);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointermove', handlePointerMove);
      };
    }, [min, max]);

    const [start, end] = [value1, value2]
      .sort(compareNumbers)
      .map(item => (100 * (item - min)) / (max - min));
    const width = `${end - start}%`;
    const left = `${start}%`;

    return (
      <Box
        ref={boxRef}
        {...props}
        sx={{ px: HANDLE_WIDTH + BORDER_WIDTH, bg: 'muted' }}
        draggable="false"
      >
        <div sx={trackStyles} draggable="false">
          <div
            sx={fillerStyles}
            style={{ left, width }}
            className="handleContainer"
            draggable="false"
          >
            <div
              sx={{ ...handleStyles, borderRadius: '2px 0 0 2px' }}
              style={{ left: -(HANDLE_WIDTH + BORDER_WIDTH) }}
              draggable="false"
            />
            <div
              sx={{ ...handleStyles, borderRadius: '0 2px 2px 0' }}
              style={{ right: -(HANDLE_WIDTH + BORDER_WIDTH) }}
              draggable="false"
            />
          </div>
        </div>
      </Box>
    );
  }
);
